import { TItem } from '@utils/filter';
import { DateRange, Duration, TCatalogFilter } from 'types/filter';
import { useIntl } from 'react-intl';
import { useAvailableFilters } from '@routes/catalog/hooks/useAvailableFilters';
import { TFilterConfig } from '@fe-learner-common';
import { formatTextsMap } from '@hooks/useSessionTranslations';
import type { TSessionFormat } from '@fe-learner-common/api/schemas/sessionsSchema';

export const useFilterConfig = ({
  items,
  filter,
}: {
  items?: TItem[];
  filter: TCatalogFilter;
}): TFilterConfig | null => {
  const intl = useIntl();
  const availableFilters = useAvailableFilters();

  if (!items) return null;

  return {
    search: {
      value: filter.search || '',
      placeholder: intl.formatMessage({
        id: 'catalog.filter.search.placeholder',
      }),
    },
    options: [
      ...(availableFilters?.date
        ? [
            {
              type: 'dateRangeChoice' as const,
              filterKey: 'date',
              label: intl.formatMessage({
                id: 'catalog.filter.date',
              }),
              selectedItems: filter.date as DateRange,
              items: availableFilters.date,
            },
          ]
        : []),
      ...(availableFilters?.duration
        ? [
            {
              type: 'durationChoice' as const,
              filterKey: 'duration',
              label: intl.formatMessage({
                id: 'catalog.filter.duration',
              }),
              selectedItems: filter.duration as Duration,
              items: availableFilters.duration,
            },
          ]
        : []),
      ...(availableFilters?.award && availableFilters.award.length > 0
        ? [
            {
              filterKey: 'award',
              label: intl.formatMessage({
                id: 'catalog.filter.award',
              }),
              type: 'multipleChoice' as const,
              selectedItems: filter.award as Set<string>,
              items: availableFilters.award.map((award) => ({
                key: award,
                label: intl.formatMessage({
                  id: `learner.common.${award}`,
                }),
              })),
            },
          ]
        : []),
      ...(availableFilters?.location && availableFilters.location.length > 0
        ? [
            {
              filterKey: 'location',
              label: intl.formatMessage({
                id: 'catalog.filter.location',
              }),
              type: 'multipleChoice' as const,
              selectedItems: filter.location as Set<string>,
              items: availableFilters.location.map((location) => ({
                key: location,
                label: location,
              })),
            },
          ]
        : []),
      ...(availableFilters?.format && availableFilters.format.length > 0
        ? [
            {
              filterKey: 'format',
              label: intl.formatMessage({
                id: 'catalog.filter.format',
              }),
              type: 'multipleChoice' as const,
              selectedItems: filter.format as Set<string>,
              items: availableFilters.format.map((format) => ({
                key: format,
                label: intl.formatMessage({
                  id: formatTextsMap[format as TSessionFormat],
                }),
              })),
            },
          ]
        : []),
      ...(availableFilters?.language && availableFilters.language.length > 0
        ? [
            {
              filterKey: 'language',
              label: intl.formatMessage({
                id: 'catalog.filter.language',
              }),
              type: 'multipleChoice' as const,
              selectedItems: filter.language as Set<string>,
              items: availableFilters.language.map((language) => ({
                key: language,
                label: language,
              })),
            },
          ]
        : []),
    ],
  };
};
